import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ModelViewerElement } from "@google/model-viewer/lib/model-viewer";
import {
  isLoggedIn,
  Link,
  LogSentryException,
  SpinnerCenter,
  useRemoteConfig,
  useRemoteConfigContext,
} from "@shapr3d/frontend-lib";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";

import { CommentCarousel } from "../../components/CommentCarousel/CommentCarousel";
import { CommentSidebar } from "../../components/CommentSidebar/CommentSidebar";
import TopAppBarFloating from "../../components/Headers/TopAppBarFloating/TopAppBarFloating";
import ModelViewerOverlay from "../../components/ModelViewerOverlay/ModelViewerOverlay";
import { ProjectPageFeatureTour } from "../../components/ProjectPageFeatureTour/ProjectPageFeatureTour";
import { ProjectSidebar } from "../../components/ProjectSidebar/ProjectSidebar";
import { ProjectSidebarMobile } from "../../components/ProjectSidebar/ProjectSidebarMobile";
import { PublishVersionModal } from "../../components/PublishVersionPromo/components/PublishVersionModal";
import { localStorageKeys } from "../../config";
import { ShareModalContextProvider } from "../../contexts/ShareModalFormContext";
import useNavigation from "../../hooks/navigation";
import { useProject } from "../../hooks/useProject";
import { useTracker } from "../../hooks/useTracker";
import { useViewLinkId, useWebExport, webExportQueryKey } from "../../hooks/useWebExport";
import { useWindowSize, ViewportType } from "../../hooks/useWindowSize";
import { ElementClickAction, ElementClickLocation } from "../../lib/TrackingAdapter";
import { SyncRequestClient } from "../../stores/requestClient";
import { useVisitedViewLinksStore } from "../../stores/visitedViewLinksStore";
import { ProjectID, ProjectRoleUtils } from "../../types";

import style from "./ModelViewerPage.module.css";

const DrawingViewer = React.lazy(() => import("../../components/DrawingViewer/DrawingViewer"));
const ModelViewer = React.lazy(() => import("../../components/ModelViewer/ModelViewer"));

const modelFileQueryKey = "modelFile";

export default function ModelViewerPage() {
  return (
    <>
      <Helmet>
        <title>Shapr3D - Published Version</title>
        <meta
          name="description"
          content="Review designs together. View models and add comments directly to your 3D design."
        />
      </Helmet>
      <ModelViewerPageComponent />
    </>
  );
}

function ModelViewerPageComponent() {
  const tracker = useTracker();
  const { isLoading: isRemoteConfigLoading } = useRemoteConfigContext();
  const { drawingId, projectId } = useParams();
  const viewLinkId = useViewLinkId();
  const addVisitedViewLink = useVisitedViewLinksStore((state) => state.addVisitedViewLink);

  const modelViewerRef = useRef<ModelViewerElement>(null);
  const queryClient = useQueryClient();

  const { viewportType } = useWindowSize();

  const parsedProjectId = projectId ? ProjectID.safeParse(projectId) : null;

  useEffect(() => {
    if (parsedProjectId?.success && viewLinkId) {
      addVisitedViewLink(parsedProjectId.data, viewLinkId);
    }
  }, [addVisitedViewLink, parsedProjectId, viewLinkId]);

  const [isNavbarOpen, setIsNavbarOpen] = useState(viewportType !== ViewportType.Mobile);
  const handleNavbarClick = useCallback(
    (isOpen: boolean) => {
      setIsNavbarOpen(isOpen);
      const isOpenText = isOpen ? "open" : "close";
      tracker.trackButtonClick({
        elementId: `navbar-${isOpenText}-button`,
        action: isOpen ? ElementClickAction.NAVBAR_OPEN : ElementClickAction.NAVBAR_CLOSE,
      });
    },
    [tracker]
  );

  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [isDrawingLoaded, setIsDrawingLoaded] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCarouselOpen, setIsCarouselOpen] = useState(false);

  useEffect(() => {
    setModelLocalURL("");
    setIsModelLoaded(false);
    setIsDrawingLoaded(false);
    setDownloadProgress(0);
    setProcessProgress(0);
    setDrawingsProgress(0);
    setIsModelViewerError(false);
    setIsDrawingViewerError(false);
    setShowFileSizeWarning(false);
    setUserAcknowledgedWarning(false);
  }, [viewLinkId]);

  useEffect(() => {
    if (viewportType === ViewportType.Tablet && isSidebarOpen) {
      setIsNavbarOpen(false);
    }
  }, [viewportType, isSidebarOpen]);

  useEffect(() => {
    if (viewportType === ViewportType.Tablet && isNavbarOpen) {
      setIsSidebarOpen(false);
    }
  }, [viewportType, isNavbarOpen]);

  useEffect(() => {
    if (viewportType !== ViewportType.Desktop && isNavbarOpen) {
      setIsCarouselOpen(false);
    }
  }, [viewportType, isNavbarOpen]);

  useEffect(() => {
    if (viewportType !== ViewportType.Desktop && isCarouselOpen) {
      setIsNavbarOpen(false);
    }
  }, [viewportType, isCarouselOpen]);

  const onModelViewerLoadHandler = useCallback(() => {
    setIsModelLoaded(true);
  }, []);

  const {
    data: webExportData,
    isLoading: isWebExportLoading,
    isSuccess: isWebExportSuccess,
  } = useWebExport();

  const { data: projectData } = useProject();

  useEffect(() => {
    setIsDrawingLoaded(false);
    setIsDrawingViewerError(false);
    setDrawingsProgress(0);
  }, [drawingId]);

  const onDrawingViewerLoadHandler = useCallback(() => {
    setIsDrawingLoaded(true);
  }, []);

  const onDrawingLoadProgressHandler = useCallback((progress: number) => {
    setDrawingsProgress(progress);
  }, []);

  const onDrawingViewerErrorHandler = useCallback(() => {
    setIsDrawingViewerError(true);
  }, []);

  const [isModelViewerError, setIsModelViewerError] = useState(false);
  const onModelViewerErrorHandler = useCallback(() => {
    if (!isModelViewerError) {
      if (viewLinkId) {
        tracker.trackModelDisplayFailed({
          webExportId: webExportData?.webExport?.webExportId,
          viewLinkId,
        });
        localStorage.removeItem(localStorageKeys.modelLoadingCrashFlag(viewLinkId));
      }
      setIsModelViewerError(true);
    }
  }, [tracker, isModelViewerError, webExportData?.webExport?.webExportId, viewLinkId]);

  const onDownloadPdfHandler = useCallback(
    async (drawingId: string) => {
      const drawingToDownload = webExportData?.webExport.drawingsDownload?.find(
        (drawing) => drawing.drawingId === drawingId
      );
      if (drawingToDownload?.download.url) {
        const res = await SyncRequestClient.publicRequest({
          method: "GET",
          url: drawingToDownload?.download.url,
        });
        const blob = new Blob([await res.blob()]);
        const blobURL = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = `${drawingToDownload.title}.pdf`;
        link.click();
        window.URL.revokeObjectURL(blobURL);
      }
    },
    [webExportData]
  );

  const [isDrawingViewerError, setIsDrawingViewerError] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [processProgress, setProcessProgress] = useState(0);
  const [drawingsProgress, setDrawingsProgress] = useState(0);
  const totalProgress = drawingId
    ? drawingsProgress
    : Math.round(downloadProgress * 0.9 + processProgress * 0.1);
  const isLoaded = drawingId ? isDrawingLoaded : isModelLoaded;

  const [modelLocalURL, setModelLocalURL] = useState("");
  useEffect(() => {
    return () => {
      URL.revokeObjectURL(modelLocalURL);
    };
  }, [modelLocalURL]);

  // This invalidate the query for the model file download URL and avoid have all models in the cache
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [modelFileQueryKey, webExportData?.webExport?.modelFileDownload?.url],
    });
  }, [queryClient, webExportData?.webExport?.modelFileDownload?.url]);

  const modelDownloadUrl = webExportData?.webExport?.modelFileDownload?.url;
  const [showFileSizeWarning, setShowFileSizeWarning] = useState(false);
  const [userAcknowledgedWarning, setUserAcknowledgedWarning] = useState(false);
  const fileSizeWarningThresholdByteFlag = useRemoteConfig("web_wv_file_size_threshold_byte-rc");
  const fileSizeThresholdByte = Number(fileSizeWarningThresholdByteFlag);
  const isWebExportDataCurrent = webExportData?.viewLinkId === viewLinkId;
  const modelFileSize = isWebExportDataCurrent ? webExportData?.webExport?.fileSize : undefined;

  const [modelCrashFlagFound, setModelCrashFlagFound] = useState<boolean | undefined>(undefined);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);

  useEffect(() => {
    if (!viewLinkId) {
      setModelCrashFlagFound(false);
      return;
    }
    const crashedViewLinkId = localStorage.getItem(
      localStorageKeys.modelLoadingCrashFlag(viewLinkId)
    );
    setModelCrashFlagFound(crashedViewLinkId === viewLinkId);
  }, [viewLinkId]);

  const shouldWarnAboutModelSizeByFileSize =
    !isNaN(fileSizeThresholdByte) &&
    fileSizeThresholdByte > 0 &&
    modelFileSize !== undefined &&
    modelFileSize > fileSizeThresholdByte;
  const shouldWarnAboutModelSize =
    modelCrashFlagFound === true || shouldWarnAboutModelSizeByFileSize;

  const modelDownloadEnabled =
    Boolean(modelDownloadUrl) &&
    modelCrashFlagFound !== undefined &&
    webExportData !== undefined &&
    isWebExportDataCurrent &&
    fileSizeWarningThresholdByteFlag !== undefined &&
    (!shouldWarnAboutModelSize || userAcknowledgedWarning);

  useEffect(() => {
    if (userAcknowledgedWarning) {
      setShowFileSizeWarning(false);
      return;
    }
    setShowFileSizeWarning(shouldWarnAboutModelSize);
  }, [shouldWarnAboutModelSize, userAcknowledgedWarning]);

  useEffect(() => {
    if (!viewLinkId) return;
    if (showFileSizeWarning && webExportData?.webExport?.webExportId) {
      tracker.trackComplexModelWarningShown({
        viewLinkId,
        webExportId: webExportData.webExport.webExportId,
      });
    }
  }, [showFileSizeWarning, tracker, viewLinkId, webExportData?.webExport?.webExportId]);

  const downloadStartTimeRef = useRef(0);
  const { isError: isModelDownloadError } = useQuery({
    queryKey: [modelFileQueryKey, modelDownloadUrl],
    queryFn: async ({ signal }) => {
      try {
        downloadStartTimeRef.current = performance.now();
        tracker.trackModelDownloadStarted({
          viewLinkId: viewLinkId,
          webExportId: webExportData?.webExport?.webExportId,
        });
        if (viewLinkId) {
          localStorage.setItem(localStorageKeys.modelLoadingCrashFlag(viewLinkId), viewLinkId);
        }

        const response = await SyncRequestClient.publicRequest({
          method: "GET",
          url: `${modelDownloadUrl}`,
          abortSignal: signal,
        });
        const blob = await response.blob((progress: number) => {
          setDownloadProgress(progress);
        });

        tracker.trackModelDownloaded({
          durationMs: Math.round(performance.now() - downloadStartTimeRef.current),
          viewLinkId,
          webExportId: webExportData?.webExport.webExportId,
        });

        const url = URL.createObjectURL(blob);
        setModelLocalURL(url);

        return url;
      } catch (error) {
        tracker.trackModelDownloadFailed({
          webExportId: webExportData?.webExport.webExportId,
          viewLinkId,
        });
        if (viewLinkId) {
          localStorage.removeItem(localStorageKeys.modelLoadingCrashFlag(viewLinkId));
        }
        LogSentryException({ messagePrefix: "Model download error", error });
        throw error;
      }
    },
    enabled: modelDownloadEnabled,
  });

  const reload = useCallback(async () => {
    setModelLocalURL("");
    setDownloadProgress(0);
    setProcessProgress(0);
    setIsModelViewerError(false);
    setIsModelLoaded(false);
    setShowFileSizeWarning(false);
    setUserAcknowledgedWarning(false);

    setIsDrawingViewerError(false);
    setIsDrawingLoaded(false);
    setDrawingsProgress(0);

    queryClient.invalidateQueries({ queryKey: webExportQueryKey });
  }, [queryClient]);

  const drawing = useMemo(
    () =>
      webExportData?.webExport?.drawingsDownload?.find?.(
        (drawing) => drawing.drawingId === drawingId
      ),
    [webExportData?.webExport?.drawingsDownload, drawingId]
  );
  const isDrawingsLayout = useMemo(
    () => Boolean(webExportData?.webExport?.drawingsDownload?.length),
    [webExportData?.webExport?.drawingsDownload]
  );

  const navigate = useNavigation();
  const hasViewOnlyAccess = isLoggedIn() && !projectData;

  const hasUnpublishedChanges = useMemo(() => {
    if (!projectData?.projectViewLinks?.length) {
      return false;
    }

    const latestRevisionId = projectData.latestRevision.revisionId;
    const maxPublishedRevisionId = Math.max(
      ...projectData.projectViewLinks.map((link) => link.revisionId)
    );
    return latestRevisionId > maxPublishedRevisionId;
  }, [projectData]);

  const unpublishedChangesWarning = hasUnpublishedChanges && (
    <div className={style.warning}>
      There are unpublished changes.
      <Link
        onClick={() => {
          tracker.trackButtonClick({
            action: ElementClickAction.PUBLISH_VERSION,
            location: ElementClickLocation.UNPUBLISHED_CHANGES_BANNER,
            details: JSON.stringify({ projectId, viewLinkId }),
          });
          setIsPublishModalOpen(true);
        }}
        className={style.link}
      >
        Publish Current Version...
      </Link>
    </div>
  );

  const viewOnlyAccessWarning = hasViewOnlyAccess && (
    <div className={style.warning}>
      You have view-only access to this project.
      <Link onClick={() => projectId && navigate.goToProject({ projectId })} className={style.link}>
        Request Edit Access...
      </Link>
    </div>
  );

  const warningMessage = unpublishedChangesWarning || viewOnlyAccessWarning || null;

  useEffect(() => {
    if (drawingId !== undefined && !drawing && isWebExportSuccess && viewLinkId && projectId) {
      navigate.goToViewLink({
        viewLinkId,
        projectId,
        replace: false,
        keepHash: true,
      });
    }
  }, [drawingId, isWebExportSuccess, drawing, viewLinkId, projectId, navigate]);

  const isEmptyState =
    !viewLinkId && projectData !== undefined && projectData.projectViewLinks.length === 0;
  const canPublish = projectData && ProjectRoleUtils.isAtLeast(projectData.userRole, "editor");
  const showOverlay = isEmptyState || (Boolean(viewLinkId) && !isLoaded);

  if (isRemoteConfigLoading) {
    return <SpinnerCenter />;
  }

  return (
    <ShareModalContextProvider>
      <div className={style.container}>
        <div className={style.modelViewerContainer}>
          <TopAppBarFloating
            modelViewer={modelViewerRef}
            isDrawingsLayout={isDrawingsLayout}
            hideARButton={isWebExportLoading}
            disableARButton={!isModelLoaded}
            downloadPdf={onDownloadPdfHandler}
            centerMessage={warningMessage}
            isOpen={isNavbarOpen}
          />

          {showOverlay && (
            <ModelViewerOverlay
              progress={totalProgress}
              error={
                (drawingId === undefined && (isModelDownloadError || isModelViewerError)) ||
                (drawingId !== undefined && isDrawingViewerError)
              }
              onRetry={reload}
              showModelSizeWarning={!drawingId && showFileSizeWarning}
              onDismissWarning={() => {
                setShowFileSizeWarning(false);
                setUserAcknowledgedWarning(true);
              }}
              warningBackground={webExportData?.revision?.thumbnailDownloads?.light?.url}
              emptyState={isEmptyState ? { canPublish: Boolean(canPublish) } : undefined}
              projectThumbnailUrl={projectData?.latestRevision?.thumbnailDownloads?.light?.url}
            />
          )}
          {!isRemoteConfigLoading && (
            <>
              {viewportType === ViewportType.Desktop && (
                <ProjectSidebar
                  isOpen={isNavbarOpen}
                  setOpen={handleNavbarClick}
                  isCommentingEnabled={Boolean(webExportData?.isCommentingEnabled) && isLoaded}
                />
              )}

              {viewportType !== ViewportType.Desktop && (
                <ProjectSidebarMobile
                  isOpen={isNavbarOpen}
                  setOpen={handleNavbarClick}
                  isCommentingEnabled={Boolean(webExportData?.isCommentingEnabled) && isLoaded}
                />
              )}
            </>
          )}

          {(!drawingId || !isDrawingsLayout) && (
            <>
              <Suspense>
                <ModelViewer
                  ref={modelViewerRef}
                  src={modelLocalURL}
                  modelDownloadURL={webExportData?.webExport?.modelFileDownload?.url}
                  onLoad={onModelViewerLoadHandler}
                  onError={onModelViewerErrorHandler}
                  onLoadProgress={(progress) => setProcessProgress(progress)}
                  isCommentingEnabled={webExportData?.isCommentingEnabled}
                />
              </Suspense>

              {viewportType === ViewportType.Desktop && (
                <CommentSidebar
                  isOpen={isSidebarOpen}
                  setIsOpen={setIsSidebarOpen}
                  isDisabled={!(webExportData?.isCommentingEnabled || !isLoggedIn())}
                />
              )}
              {viewportType !== ViewportType.Desktop && webExportData?.isCommentingEnabled && (
                <CommentCarousel isOpen={isCarouselOpen} setIsOpen={setIsCarouselOpen} />
              )}
            </>
          )}
          {drawingId && isDrawingsLayout && drawing && (
            <Suspense>
              <DrawingViewer
                key={drawing.download.url}
                drawing={drawing}
                onLoad={onDrawingViewerLoadHandler}
                onLoadProgress={onDrawingLoadProgressHandler}
                onError={onDrawingViewerErrorHandler}
              ></DrawingViewer>
            </Suspense>
          )}
        </div>
        <div className={style.bottomBg}></div>
      </div>
      <PublishVersionModal open={isPublishModalOpen} onClose={() => setIsPublishModalOpen(false)} />
      {!viewLinkId && <ProjectPageFeatureTour />}
    </ShareModalContextProvider>
  );
}
